import { FC } from "react"
import { Formik } from "formik"
import { object, string } from "yup"
import { RateTableConfigDetails } from "../../../types"
import ManageRateTableFormContent from "../../../common/ui/config-admin/config-rate-table/manage/form-content"
import Tabs from "../../../common/ui/tabs"
import ConfigEditHeader from "../../../common/ui/config-admin/config-edit-header"
import {
  useDeleteRateTableConfigMutation,
  useFetchDefaultRateTableConfigQuery,
  useFetchRateTableConfigDetailsQuery,
  useUpdateOrCreateRateTableConfigMutation
} from "../../../redux/queries"
import { ConfigEditPageSkeleton } from "../../../common/ui/skeletons"
import { useConfigEditPage } from "../use-config-edit-page"

const TABS = [
  { id: "general", label: "General" },
  { id: "terms", label: "Terms" },
  { id: "presets", label: "Presets" },
  { id: "cta", label: "CTA" },
  { id: "advanced", label: "Advanced" }
]

const RateTableSchema = object({
  name: string().required().min(1),
  details: object().required(),
  brand_id: string().required().min(1)
})

const RateTableEditPage: FC = () => {
  const [updateOrCreate, saveResult] =
    useUpdateOrCreateRateTableConfigMutation()
  const [deleteRateTable, deleteResult] = useDeleteRateTableConfigMutation()

  const {
    id,
    isCreate,
    formRef,
    setFormField,
    tab,
    setTab,
    brands,
    navigateBack
  } = useConfigEditPage<RateTableConfigDetails>({
    tabs: TABS,
    tab: "rate-table",
    label: "rate table",
    saveResult,
    deleteResult
  })

  const { data: rateTableData, isLoading } =
    useFetchRateTableConfigDetailsQuery(
      { id: id ?? "" },
      { skip: isCreate || !id }
    )

  const { data: defaultData } = useFetchDefaultRateTableConfigQuery(
    {},
    { skip: !isCreate }
  )

  const raw = rateTableData ?? {
    name: "",
    id: "",
    brand_id: "",
    ...(defaultData || { is_default: "0", details: {} })
  }

  const initialValues: RateTableConfigDetails = {
    ...raw,
    details: raw.details ?? {}
  }

  const onSubmit = () => {
    if (formRef.current?.values) updateOrCreate(formRef.current.values)
  }

  const onDelete = () => {
    if (rateTableData?.id) deleteRateTable(rateTableData.id)
  }

  if (!isCreate && isLoading) {
    return <ConfigEditPageSkeleton tabCount={5} />
  }

  const pageTitle = isCreate
    ? "New rate table"
    : rateTableData?.name ?? "Rate table"

  return (
    <div className="min-h-screen bg-gray-50">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        innerRef={formRef}
        validationSchema={RateTableSchema}
        enableReinitialize
      >
        {({ values, isValid, dirty }) => (
          <>
            <ConfigEditHeader
              allowEditName
              backLabel="Rate tables"
              namePlaceholder="Rate table name"
              name={values.name}
              pageTitle={pageTitle}
              isCreate={isCreate}
              isValid={isValid}
              dirty={dirty}
              isDefault={values.is_default === "1"}
              brands={brands ?? []}
              brandId={values.brand_id}
              onNameChange={val => setFormField("name", val)}
              onDefaultChange={v => setFormField("is_default", v ? "1" : "0")}
              onBrandChange={val => setFormField("brand_id", val)}
              onDelete={onDelete}
              onCancel={() => navigateBack(values.brand_id)}
              onSubmit={onSubmit}
              onBack={() => navigateBack(values.brand_id)}
              saving={saveResult.isLoading}
            />

            <Tabs
              currentTab={tab}
              tabs={TABS}
              onChange={setTab}
              className="px-6 bg-white"
            />

            <div className="bg-white">
              <div className="max-w-2xl py-6 px-6">
                <ManageRateTableFormContent
                  values={values}
                  tab={tab}
                  setFormField={setFormField}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </div>
  )
}

export default RateTableEditPage
